import { useEffect, useRef } from "react";
import type { User } from "@ferrivia/api-contract";
import { localeSchema, useSyncUserLocale, useTranslation } from "@ferrivia/i18n";
import { useApi } from "./AuthProvider";

/**
 * Two-way wiring between the authenticated user's stored locale and the
 * running i18n instance: applies `user.locale` to i18n (unless a `?lang=`/
 * localStorage override already picked one — see `useSyncUserLocale`), and,
 * when `user.locale` is still null (never chosen), PATCHes the resolved
 * locale back once so the field is populated for a future switcher.
 */
export function useLocaleSync(user: User): void {
  const api = useApi();
  const { i18n } = useTranslation();
  useSyncUserLocale(i18n, user.locale);

  const backfilled = useRef(false);
  useEffect(() => {
    if (user.locale || backfilled.current) return;
    const parsed = localeSchema.safeParse(i18n.language);
    if (!parsed.success) return;
    backfilled.current = true;
    void api.updateMe({ locale: parsed.data }).catch(() => {
      backfilled.current = false;
    });
  }, [api, i18n, user.locale]);
}
